import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Input,
    Flex,
    Text,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Select,
    VStack,
    HStack,
    Tag,
    Spinner,
    useToast,
    Center
} from '@chakra-ui/react';
import { BiCheckCircle } from 'react-icons/bi';
import { BsActivity, BsArrowLeft, BsClock, BsPause, BsSend } from 'react-icons/bs';
import Pagination from 'react-js-pagination';
import BoxWrapper from '../../../components/BoxWrapper/BoxWrapper';
import Header from '../../../components/Header';
import API from '../../../helpers/axiosInstance';
import { getApiHeaders, hasPermission } from '../../../utils/DefaultFunction';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

export default function FlowExecution() {
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [flowData, setFlowData] = useState(null);
    const [executions, setExecutions] = useState([]);
    const [totalExecutions, setTotalExecutions] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [stats, setStats] = useState({
        total: 0,
        successful: 0,
        stopped: 0,
        totalMessages: 0,
    });
    const itemsPerPage = 10;
    const toast = useToast();
    const { id } = useParams();
    // Get flow_id from URL params or state
    const flowId = id // Replace with actual flow ID from route params
    const navigate = useNavigate();
    const location = useLocation();
    const [permissions, setPermissions] = useState([]);
    const [permissionsLoading, setPermissionsLoading] = useState(true);
    // Backend groups every flow-related permission under the single "flow"
    // feature slug, not this page's own route (which includes the record id).
    const permParams = "flow";

    const fetchPermissions = async () => {
        setPermissionsLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/permission/feature/list?slug=${permParams}`;
            const response = await API.get(url, { headers: getApiHeaders() });
            setPermissions(response.data.data || []);
        } catch (error) {
            console.error("Error fetching permissions:", error);
            setPermissions([]);
        } finally {
            setPermissionsLoading(false);
        }
    };
    useEffect(() => {
        fetchPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const flowExecutionViewPermission = hasPermission("flow-execution-view", permissions);
    const flowExecutionDetailPermission = hasPermission("flow-execution-detail", permissions);
    // Dummy flow info - you can fetch this from API or pass via route
    const flow = {
        id: flowId,
        name: flowData?.flow_name || 'Welcome Flow',
        trigger: flowData?.trigger_name || 'User Signup',
        status: flowData?.status || 'active',
    };

    const fetchHistory = async (page = 1, search = '') => {
        setLoading(true);
        try {
            const url = `${process.env.REACT_APP_FLOW_API_URL}/flow/execution-list?id=${flowId}&page=${page}&limit=${itemsPerPage}&search=${search}`;
            const response = await API.get(url, { headers: getApiHeaders() });

            if (response.data.success) {
                setFlowData(response.data);
                setExecutions(response.data.executions || []);
                setTotalExecutions(response.data.total || 0);
                setTotalPages(response.data.totalPages || 0);

                // Update stats
                setStats({
                    total: response.data.api_stat?.total_execution || 0,
                    successful: response.data.api_stat?.total_success || 0,
                    stopped: (response.data.api_stat?.total_execution || 0) - (response.data.api_stat?.total_success || 0),
                    totalMessages: response.data.executions?.reduce((acc, e) => {
                        const sent = parseInt(e.message_sent.split('/')[0]) || 0;
                        return acc + sent;
                    }, 0) || 0,
                });
            }
        } catch (error) {
            console.error('Error fetching execution history:', error);
            toast({
                title: 'Error',
                description: 'Failed to fetch execution history',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    // Single useEffect for both page and search changes
    useEffect(() => {
        if (permissionsLoading || !flowExecutionViewPermission) return;
        const timeoutId = setTimeout(() => {
            fetchHistory(currentPage, searchTerm);
        }, searchTerm ? 500 : 0); // Debounce only for search, immediate for page change

        return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, searchTerm, permissionsLoading, flowExecutionViewPermission]);

    // Filter executions by status (client-side only)
    const filteredExecutions = executions.filter((e) => {
        if (statusFilter === 'all') return true;

        const status = e.execution_status.toLowerCase();
        if (statusFilter === 'completed') return status === 'completed';
        if (statusFilter === 'in_progress') return status === 'in progress' || status === 'in_progress';
        if (statusFilter === 'stopped') return status === 'stopped' || status === 'failed';

        return true;
    });

    // Format date
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    // Tag for status
    const getStatusTag = (status) => {
        const normalizedStatus = status.toLowerCase();

        if (normalizedStatus === 'completed') {
            return (
                <Tag color={'#008236'} px={2} bg={'#f0fdf4'} gap={1} border={'1px solid #97e0b6'}>
                    <BiCheckCircle size={14} className="inline mr-1" /> Completed
                </Tag>
            );
        }
        if (normalizedStatus === 'in progress' || normalizedStatus === 'in_progress') {
            return (
                <Tag color={'var(--brand-primary-hover)'} bg={'var(--brand-tint)'} gap={'1'} border={'1px solid var(--brand-tint-strong)'} px={2}>
                    <BsClock size={14} className="inline mr-1" /> In Progress
                </Tag>
            );
        }
        return (
            <Tag color={'#364153'} bg={'#fbf9fa'} border={'1px solid #ebe6e7'} gap={1} px={2}>
                <BsPause size={14} className="inline mr-1" /> Stopped
            </Tag>
        );
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // Handle search input change
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1); // Reset to first page when searching
    };

    const handleBack = () => {
        navigate('/flow');
    };

    const handleViewDetail = (id) => {
        navigate(`/flow-execution-detail/${id}`);
    };

    if (permissionsLoading) {
        return (
            <>
                <Header />
                <BoxWrapper>
                    <Center h="400px">
                        <Spinner size="xl" color="blue.500" thickness="4px" />
                    </Center>
                </BoxWrapper>
            </>
        );
    }
    return (
        <>
            <Header />
            <BoxWrapper>
                {flowExecutionViewPermission ? (
                <VStack spacing={6} align="stretch">
                    {/* Header */}
                    <Flex align="flex-start" direction={'column'} gap={2}>
                        <Button leftIcon={<BsArrowLeft />} variant="outline" size="sm" onClick={handleBack}   _hover={{ bg: "#0000001a !important", color: "black !important" }}>
                            Back to Flows
                        </Button>
                        <HStack spacing={2}>
                            <BsActivity size={28} color="var(--brand-primary)" />
                            <Text fontSize="xl" fontWeight="bold">
                                Execution History
                            </Text>
                        </HStack>
                    </Flex>

                    {/* Flow Info */}
                    <Box className="box-style-card">
                        <Flex gap={6} fontSize="sm">
                            <Box>
                                <Text as="span" color="gray.500">
                                    Flow Name:
                                </Text>
                                <Text as="span" ml={2} fontWeight="medium">
                                    {flow.name}
                                </Text>
                            </Box>
                            <Box>
                                <Text as="span" color="gray.500">
                                    Trigger:
                                </Text>
                                <Text as="span" ml={2}>
                                    {flow.trigger}
                                </Text>
                            </Box>
                            <Box>
                                <Text as="span" color="gray.500">
                                    Status:
                                </Text>
                                <Tag
                                    ml={2}

                                    colorScheme={flow.status === 'active' ? 'green' : 'gray'}
                                >
                                    {flow.status === 'active'
                                        ? 'Active'
                                        : flow.status === 'inactive'
                                            ? 'Inactive'
                                            : 'Draft'}
                                </Tag>
                            </Box>
                        </Flex>
                    </Box>

                    {/* Stats */}
                    <Flex gap={4} align="stretch">
                        <Box
                            className="box-style-card"
                            flex={1}
                            bg="white"
                            borderWidth="1px"
                            borderColor="gray.200"
                            borderRadius="lg"
                            p={4}
                        >
                            <Flex justify="space-between" align="center">
                                <Box>
                                    <Text fontSize="sm" color="gray.500">
                                        Total Executions
                                    </Text>
                                    <Text fontSize="2xl">{stats.total}</Text>
                                </Box>
                                <BsActivity size={32} color="gray" />
                            </Flex>
                        </Box>

                        <Box
                            className="box-style-card"
                            flex={1}
                            bg="white"
                            borderWidth="1px"
                            borderColor="gray.200"
                            borderRadius="lg"
                            p={4}
                        >
                            <Flex justify="space-between" align="center">
                                <Box>
                                    <Text fontSize="sm" color="gray.500">
                                        Successful Executions
                                    </Text>
                                    <Text fontSize="2xl" color="green.600">
                                        {stats.successful}
                                    </Text>
                                </Box>
                                <BiCheckCircle size={32} color="green" />
                            </Flex>
                        </Box>

                        <Box
                            className="box-style-card"
                            flex={1}
                            bg="white"
                            borderWidth="1px"
                            borderColor="gray.200"
                            borderRadius="lg"
                            p={4}
                        >
                            <Flex justify="space-between" align="center">
                                <Box>
                                    <Text fontSize="sm" color="gray.500">
                                        Stopped / Exited
                                    </Text>
                                    <Text fontSize="2xl" color="gray.600">
                                        {stats.stopped}
                                    </Text>
                                </Box>
                                <BsPause size={32} color="gray" />
                            </Flex>
                        </Box>

                        <Box
                            className="box-style-card"
                            flex={1}
                            bg="white"
                            borderWidth="1px"
                            borderColor="gray.200"
                            borderRadius="lg"
                            p={4}
                        >
                            <Flex justify="space-between" align="center">
                                <Box>
                                    <Text fontSize="sm" color="gray.500">
                                        Total Messages Sent
                                    </Text>
                                    <Text fontSize="2xl" color="var(--brand-primary)">
                                        {stats.totalMessages}
                                    </Text>
                                </Box>
                                <BsSend size={32} color="var(--brand-primary)" />
                            </Flex>
                        </Box>
                    </Flex>

                    {/* Filters */}
                    <Box className="box-style-card">
                        <Box>
                            <Flex gap={4}>
                                <Box flex={1} position="relative">
                                    <Input
                                        pl="30px"
                                        placeholder="Search Execution ID..."
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                    />
                                </Box>
                                <Select
                                    w="180px"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="all">All Status</option>
                                    <option value="completed">Completed</option>
                                    <option value="in_progress">In Progress</option>
                                    <option value="stopped">Stopped</option>
                                </Select>
                            </Flex>
                        </Box>
                    </Box>

                    {/* Table */}
                    <Box className="box-style-card">
                        <Box p={0}>
                            {loading ? (
                                <Flex justify="center" py={12}>
                                    <Spinner size="xl" color="var(--brand-primary)" />
                                </Flex>
                            ) : filteredExecutions.length === 0 ? (
                                <Flex justify="center" py={12} direction="column" align="center">
                                    <BsActivity size={64} color="gray" className="mb-4" />
                                    <Text color="gray.500" fontSize="lg">
                                        No executions yet
                                    </Text>
                                    <Text color="gray.500" fontSize="sm">
                                        This flow hasn't been triggered.
                                    </Text>
                                </Flex>
                            ) : (
                                <Table variant="simple">
                                    <Thead bg="gray.50">
                                        <Tr>
                                            <Th>Execution ID</Th>
                                            <Th>Contact Number</Th>
                                            <Th>Triggered At</Th>
                                            <Th>Trigger Source</Th>
                                            <Th textAlign="center">Messages</Th>
                                            <Th>Status</Th>
                                            <Th>Duration</Th>
                                            <Th textAlign="right">Action</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {filteredExecutions.map((exec) => (
                                            <Tr key={exec.execution_id}>
                                                <Td>
                                                    <Text
                                                        variant="link"
                                                        color={flowExecutionDetailPermission ? 'var(--brand-primary)' : 'gray.600'}
                                                        cursor={flowExecutionDetailPermission ? 'pointer' : 'default'}
                                                        onClick={flowExecutionDetailPermission ? () => handleViewDetail(exec.execution_id) : undefined}
                                                    >
                                                        {exec.execution_id}
                                                    </Text>
                                                </Td>
                                                 <Td>{exec.contact_no}</Td>
                                                <Td>{formatDate(exec.triggered_at)}</Td>
                                               
                                                <Td>{exec.trigger_source}</Td>
                                                <Td textAlign="center">
                                                    <Tag fontFamily="monospace">
                                                        {exec.message_sent}
                                                    </Tag>
                                                </Td>
                                                <Td>{getStatusTag(exec.execution_status)}</Td>
                                                <Td>{exec.duration}</Td>
                                                <Td textAlign="right">
                                                    {flowExecutionDetailPermission && (
                                                        <Button size="xs" variant="outline" colorScheme="blue" color={"var(--brand-primary-hover)"} _hover={{ bg: "#e2e8f0 !important", color: "var(--brand-primary-active) !important" }} onClick={() => handleViewDetail(exec.execution_id)}>
                                                            View Details
                                                        </Button>
                                                    )}
                                                </Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            )}
                        </Box>
                    </Box>

                    {/* Pagination */}
                    {totalPages > 1 && !loading && (
                        <Flex justify="center" align="center">

                            <Pagination
                                activePage={currentPage}
                                itemsCountPerPage={itemsPerPage}
                                totalItemsCount={totalExecutions}
                                pageRangeDisplayed={5}
                                onChange={handlePageChange}
                                itemClass="page-item"
                                linkClass="page-link"
                                activeClass="active"
                                activeLinkClass="active-link"
                            />
                        </Flex>
                    )}
                </VStack>
                ) : (
                    <Center h="400px">
                        <Box textAlign="center">
                            <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>
                                No Access
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                                You don't have permission to view this sections.
                            </Text>
                        </Box>
                    </Center>
                )}
            </BoxWrapper>
        </>
    );
}